import { useCallback, useEffect, useMemo, useState } from 'react';
import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import config from '../aws-config';

function decodeJwt(token) {
  if (!token) {
    return {};
  }

  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch (error) {
    console.error('Failed to decode Cognito token', error);
    return {};
  }
}

function challengeError(challenge, message, cognitoUser = null) {
  const error = new Error(message);
  error.code = 'NewPasswordRequiredException';
  error.challenge = challenge;
  error.cognitoUser = cognitoUser;
  return error;
}

function normalizeUser(cognitoUser, session) {
  const accessPayload = decodeJwt(session.getAccessToken().getJwtToken());
  const idPayload = decodeJwt(session.getIdToken().getJwtToken());
  const groups = accessPayload['cognito:groups'] || idPayload['cognito:groups'] || [];

  return {
    username: cognitoUser.getUsername(),
    email: idPayload.email || cognitoUser.getUsername(),
    groups: Array.isArray(groups) ? groups : String(groups).split(',').filter(Boolean),
    sub: idPayload.sub,
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
    expiresAt: accessPayload.exp ? accessPayload.exp * 1000 : null,
  };
}

export default function useAuth() {
  const pool = useMemo(
    () =>
      new CognitoUserPool({
        UserPoolId: config.userPoolId,
        ClientId: config.clientId,
      }),
    [],
  );
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingChallenge, setPendingChallenge] = useState(null);

  const syncUserFromSession = useCallback(() => {
    const cognitoUser = pool.getCurrentUser();

    if (!cognitoUser) {
      setCurrentUser(null);
      setLoading(false);
      return;
    }

    cognitoUser.getSession((error, session) => {
      if (error || !session?.isValid()) {
        console.error('Failed to restore Cognito session', error);
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      setCurrentUser(normalizeUser(cognitoUser, session));
      setLoading(false);
    });
  }, [pool]);

  useEffect(() => {
    syncUserFromSession();
  }, [syncUserFromSession]);

  const login = useCallback(
    (username, password) =>
      new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: username, Pool: pool });
        const authDetails = new AuthenticationDetails({ Username: username, Password: password });

        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session) => {
            setPendingChallenge(null);
            const user = normalizeUser(cognitoUser, session);
            setCurrentUser(user);
            resolve(user);
          },
          onFailure: (error) => {
            setPendingChallenge(null);
            reject(error);
          },
          newPasswordRequired: (userAttributes, requiredAttributes) => {
            setPendingChallenge({
              type: 'NEW_PASSWORD',
              cognitoUser,
              userAttributes,
              requiredAttributes,
            });
            reject(challengeError('NEW_PASSWORD', 'A password reset is required before this user can sign in.', cognitoUser));
          },
        });
      }),
    [pool],
  );

  const completeNewPassword = useCallback(
    (newPassword, userAttributes = {}) =>
      new Promise((resolve, reject) => {
        if (!pendingChallenge || pendingChallenge.type !== 'NEW_PASSWORD') {
          reject(new Error('No pending new-password challenge.'));
          return;
        }

        const { cognitoUser } = pendingChallenge;
        cognitoUser.completeNewPasswordChallenge(
          newPassword,
          userAttributes,
          {
            onSuccess: (session) => {
              setPendingChallenge(null);
              const user = normalizeUser(cognitoUser, session);
              setCurrentUser(user);
              resolve(user);
            },
            onFailure: (error) => {
              reject(error);
            },
          },
        );
      }),
    [pendingChallenge],
  );

  const forgotPassword = useCallback(
    (username) =>
      new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: username, Pool: pool });
        cognitoUser.forgotPassword({
          onSuccess: (data) => resolve(data),
          onFailure: (error) => reject(error),
        });
      }),
    [pool],
  );

  const confirmPasswordReset = useCallback(
    (username, code, newPassword) =>
      new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: username, Pool: pool });
        cognitoUser.confirmPassword(code, newPassword, {
          onSuccess: (data) => resolve(data),
          onFailure: (error) => reject(error),
        });
      }),
    [pool],
  );

  const logout = useCallback(() => {
    const cognitoUser = pool.getCurrentUser();
    cognitoUser?.signOut();
    setCurrentUser(null);
  }, [pool]);

  const getToken = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const cognitoUser = pool.getCurrentUser();
        if (!cognitoUser) {
          resolve(null);
          return;
        }
        cognitoUser.getSession((error, session) => {
          if (error || !session?.isValid()) {
            resolve(null);
            return;
          }
          // API Gateway COGNITO_USER_POOLS authorizer validates ID tokens, not access tokens
          resolve(session.getIdToken().getJwtToken());
        });
      }),
    [pool],
  );

  const clearPendingChallenge = useCallback(() => {
    setPendingChallenge(null);
  }, []);

  return {
    currentUser,
    loading,
    pendingChallenge,
    login,
    logout,
    getToken,
    completeNewPassword,
    clearPendingChallenge,
    forgotPassword,
    confirmPasswordReset,
    refreshSession: syncUserFromSession,
  };
}
